import { Text, Input, Separator, Icon, Label } from '@flatfile/baseline'
import React, { useRef, useState } from 'react'
import { copyToClipboard } from '@flatfile/shared-ui'
import { AppHTTPService } from '../../api/http-service'
import { logger } from '../../utils/logging'

export async function getEnvironmentApiKeys(environmentId: string) {
  try {
    const response = await AppHTTPService.client.auth.getApiKeys({ environmentId })
    const keys = response.data
    const returnedKeys = { PUBLISHABLE: '', SECRET: '' }
    keys?.forEach((key: any) => {
      if (key.type === 'PUBLISHABLE') returnedKeys.PUBLISHABLE = key.rawKey
      if (key.type === 'SECRET') returnedKeys.SECRET = key.rawKey
    })
    return returnedKeys
  } catch (err) {
    logger.error('Error fetching getEnvironmentApiKeys', { error: err })
    return { PUBLISHABLE: '', SECRET: '' }
  }
}

export function EnvironmentAPIKeys(props: { environment: any; apiKeys?: any }) {
  const { environment, apiKeys: propApiKeys } = props

  const [skVisible, setSkVisible] = useState(false)
  const [copiedStates, setCopiedStates] = useState({
    envName: false,
    envId: false,
    publishableKey: false,
    secretKey: false,
  })

  const toggle = () => setSkVisible((prev) => !prev)

  const changeVisibility = (ref: React.RefObject<HTMLInputElement>) =>
    ref.current ? toggle() : logger.error('changeVisibility error - No ref found')

  const handleCopy = (value: string, key: keyof typeof copiedStates) => {
    copyToClipboard(value)

    // Set the copied state for this specific button
    setCopiedStates((prev) => ({ ...prev, [key]: true }))

    // Reset the copied state after a delay
    setTimeout(() => {
      setCopiedStates((prev) => ({ ...prev, [key]: false }))
    }, 1000)
  }

  const pkRef = useRef<HTMLInputElement>(null)
  const skRef = useRef<HTMLInputElement>(null)
  const envNameRef = useRef<HTMLInputElement>(null)
  const idRef = useRef<HTMLInputElement>(null)

  // If no apiKeys are provided, return null
  if (!propApiKeys) return null

  return (
    <>
      <div className='grid grid-cols-12 items-center p-3'>
        <div className='col-span-4 pr-6'>
          <Text variant='title3'>Environment Information</Text>
          <br />
          <Text variant='callout' className='mt-2 text-gray-500'>
            Each environment has a unique ID and name. The ID is used to identify the environment in the API and get
            information from it.
          </Text>
        </div>
        <div className='col-span-7 col-start-6'>
          <div className='mb-4'>
            <Label>Environment Name</Label>
            <div className='mt-3 relative'>
              <Input value={environment.name} ref={envNameRef} disabled className='pr-8' />
              <button
                type='button'
                className='absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors duration-150'
                onClick={() => handleCopy(environment.name, 'envName')}
              >
                <Icon name={copiedStates.envName ? 'checkmark-2' : 'copy-squares'} size={16} />
              </button>
            </div>
          </div>

          <div className='mt-4'>
            <Label>Environment ID</Label>
            <div className='mt-3 relative'>
              <Input value={environment.id} ref={idRef} disabled className='pr-8' />
              <button
                type='button'
                className='absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors duration-150'
                onClick={() => handleCopy(environment.id, 'envId')}
              >
                <Icon name={copiedStates.envId ? 'checkmark-2' : 'copy-squares'} size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
      <Separator className='my-4' />
      <div className='grid grid-cols-12 p-3 items-center'>
        <div className='col-span-4 pr-6'>
          <Text variant='title3'>API Keys</Text>
          <br />
          <Text variant='callout' className='mt-2 text-gray-500 '>
            Each Account includes two types of keys for every environment. Only use your secret key exclusively within
            your server-side code. Your publishable keys can be safely accessed within your application's client-side
            code.
          </Text>
        </div>
        <div className='col-span-7 col-start-6'>
          <div className='mb-4'>
            <Label>Publishable Key</Label>
            <br />
            <Text variant='callout' className='text-gray-500 text-xs'>
              Safe to copy into a public webpage. <br /> Cannot be used to read the API and can only be used for
              creating a new space.
            </Text>
            <div className='mt-1.5 relative'>
              <Input value={propApiKeys.PUBLISHABLE} ref={pkRef} disabled className='pr-8' />
              <button
                type='button'
                className='absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors duration-150'
                onClick={() => handleCopy(propApiKeys.PUBLISHABLE, 'publishableKey')}
              >
                <Icon name={copiedStates.publishableKey ? 'checkmark-2' : 'copy-squares'} size={16} />
              </button>
            </div>
          </div>

          <div className='mt-4'>
            <Label>Secret Key</Label>
            <br />
            <Text variant='callout' className='text-gray-500 text-xs'>
              Keep this key safe. <br /> You can use this key to administrate, read and write any resource in this
              environment.
            </Text>
            <div className='mt-1.5 relative'>
              <Input
                type={skVisible ? 'text' : 'password'}
                value={propApiKeys.SECRET}
                ref={skRef}
                disabled
                className='pr-16'
              />
              <div className='absolute right-2 top-1/2 -translate-y-1/2 flex'>
                <button
                  type='button'
                  className='text-gray-500 hover:text-gray-700 mr-2'
                  onClick={() => changeVisibility(skRef)}
                >
                  {skVisible ? <Icon name='eye-slash' size={16} /> : <Icon name='eye-open' size={16} />}
                </button>
                <button
                  type='button'
                  className='text-gray-500 hover:text-gray-700 transition-colors duration-150'
                  onClick={() => handleCopy(propApiKeys.SECRET, 'secretKey')}
                >
                  <Icon name={copiedStates.secretKey ? 'checkmark-2' : 'copy-squares'} size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
